import { useCallback, useMemo } from 'react';
import userService from '../services/user.service';
import { AUTHENTICATION_TYPE, SIGN_IN_FLOW_ERROR_CODE } from '../constants';
import { GENERIC_SERVER_ERROR_MESSAGE } from '../constants/applicationErrorCodes';
import { unlockOrganizationWith } from '../utils/unlock-organization';

const SIGN_IN_ERROR_CODE_TO_MESSAGE = new Map([
  [SIGN_IN_FLOW_ERROR_CODE.USER_DEACTIVATED, welcomeLang => welcomeLang.USER_DEACTIVATED],
  [SIGN_IN_FLOW_ERROR_CODE.SSO_MISCONFIGURED, welcomeLang => welcomeLang.SSO_ERROR_MESSAGE],
  [SIGN_IN_FLOW_ERROR_CODE.GENERIC_SERVER_ERROR, () => GENERIC_SERVER_ERROR_MESSAGE],
]);

// Unmapped codes fall back to FAILED_TO_AUTHENTICATION.
export const getSignInFlowErrorMessage = (code, welcomeLang) =>
  (SIGN_IN_ERROR_CODE_TO_MESSAGE.get(code) ?? (lang => lang.FAILED_TO_AUTHENTICATION))(welcomeLang);

/**
 * @param {object} deps
 * @param {(params: { authenticationMethodId, organizationId, loginHint }) => Promise<void>} deps.redirectToSSO
 * @param {(params: { provider, loginHint }) => Promise<{ error?: string } | void>} deps.redirectToOauth
 *   May return `{ error }` instead of redirecting; the hook forwards it to the caller.
 */
export function useSignInFlow({ redirectToSSO, redirectToOauth }) {
  const startSignIn = useCallback(
    async ({ email }) => {
      const result = await userService.resolveSignInFlow({ email });

      if (result.error) {
        return result;
      }

      switch (result.authType) {
        case AUTHENTICATION_TYPE.SAML: {
          const { authenticationMethodId, organizationId, loginHint } = result;
          await redirectToSSO({ authenticationMethodId, organizationId, loginHint });
          return result;
        }
        case AUTHENTICATION_TYPE.OAUTH: {
          const { provider, loginHint } = result;
          const oauthResult = await redirectToOauth({ provider, loginHint });
          if (oauthResult?.error) {
            return oauthResult;
          }
          return result;
        }
        case AUTHENTICATION_TYPE.OTP:
        default:
          return result;
      }
    },
    [redirectToSSO, redirectToOauth],
  );

  return { startSignIn };
}

/**
 * Memoized `unlockOrganizationWith` for components; see `shared/utils/unlock-organization`.
 * Pass stable (memoized) deps so the returned callback stays stable across renders.
 */
export function useUnlockOrganization({ redirectToSSO, unlockWithOauth, unlockWithOtp }) {
  return useMemo(
    () => unlockOrganizationWith({ redirectToSSO, unlockWithOauth, unlockWithOtp }),
    [redirectToSSO, unlockWithOauth, unlockWithOtp],
  );
}
